import { useMemo } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useWorkflows } from '@/lib/workflows-store';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import { usePodeAcessar, useMeusAcessos } from '@/lib/acesso-store';
import { chaveDaRota } from '@/lib/acesso-rotas';
import { useRhNaTela } from '@/lib/rh/use-rh';
import { RH } from '@/lib/rh/textos';
import {
  filterDepartamentosPorUser,
  decidirDepartamentosVisiveis,
} from '@/components/layout/sidebar-dept-filter';
import { DEPARTAMENTOS_NAV, agruparWorkflows, montarNavegacao, type SecaoNav } from './navegacao';

/**
 * Lê permissões, flags e workflows e devolve as seções do menu — a MESMA
 * resposta para o menu lateral e para a navegação do celular (M1).
 *
 * Esconder no menu é conforto; quem barra de verdade é a API (`requireTela`).
 */
export function useNavegacao(): SecaoNav[] {
  const { user } = useAuth();
  // React Query deduplica entre o menu lateral, o celular e as páginas.
  const { data: allWorkflows = [] } = useWorkflows({ ativo: true });
  const workflowsPorDept = useMemo(() => agruparWorkflows(allWorkflows), [allWorkflows]);

  // BUG-004: filtra pelo dept do usuário. Super-admin sempre vê todos.
  const porDepartamento = useMemo(
    () => filterDepartamentosPorUser(DEPARTAMENTOS_NAV, user?.departments, user?.isSuperAdmin ?? false),
    [user?.departments, user?.isSuperAdmin],
  );
  // S-4: onde a rota tem CHAVE mapeada, vale a permissão calculada.
  const { itens: meusAcessos, carregando: acessosCarregando } = useMeusAcessos();
  const departamentosVisiveis = useMemo(
    () =>
      decidirDepartamentosVisiveis({
        todos: DEPARTAMENTOS_NAV,
        visiveisPorDepartamento: porDepartamento,
        chaveDaRota,
        temChave: (chave) => meusAcessos.some((i) => i.chave === chave && i.permitido),
        acessosCarregando,
        isAdmin: (user?.isAdmin ?? false) || (user?.isSuperAdmin ?? false),
      }),
    [porDepartamento, meusAcessos, acessosCarregando, user?.isAdmin, user?.isSuperAdmin],
  );

  // CO-1: Compras restrito ao setor (ou super-admin), nunca fail-open.
  const comprasEnabled = useMyFeatureFlag('COMPRAS_ENABLED');
  const compras =
    comprasEnabled && (user?.isSuperAdmin === true || (user?.departments?.includes('compras') ?? false));

  // MONITORAMENTO: a FLAG decide se existe, a CHAVE decide quem entra.
  const monitoramentoEnabled = useMyFeatureFlag('MONITORAMENTO_ENABLED');
  const podeMonitoramento = usePodeAcessar('manutencao.monitoramento.ver');
  const podeComunicados = usePodeAcessar('comunicados.ver');
  // RH-MENU (R1): item próprio da seção Apoio — flag + `podeAbrirRh` do servidor.
  const { podeAbrir: podeAbrirRh } = useRhNaTela();
  const podeIndicadoresManutencao = usePodeAcessar('manutencao.indicadores.ver');
  const podeOsDoSistema = usePodeAcessar('manutencao.os.ver');

  const crmV2 = useMyFeatureFlag('CRM_V2_ENABLED');
  const orcamentoLigado = useMyFeatureFlag('ORCAMENTO_ENABLED');
  // PERMISSOES-ACESSO (P3): cada sub-item do Comercial aparece pela CHAVE.
  const leads = usePodeAcessar('comercial.leads.ver');
  const funil = usePodeAcessar('comercial.funil.ver');
  const indicadores = usePodeAcessar('comercial.indicadores.ver');
  const orcamentos = usePodeAcessar('comercial.orcamentos.ver');
  const metas = usePodeAcessar('comercial.metas.ver');
  const excluir = usePodeAcessar('comercial.excluir');
  const podeAlgoNoComercial = funil || leads || indicadores || orcamentos || metas;
  const comercialJaNaLista = departamentosVisiveis.some((d) => d.slug === 'comercial');
  const mostrarComercialAvulso =
    crmV2 && !user?.isAdmin && !user?.isSuperAdmin && !comercialJaNaLista && podeAlgoNoComercial;

  const financeiroVer = usePodeAcessar('financeiro.ver');
  const financeiroAgir = usePodeAcessar('financeiro.agir');

  return useMemo(
    () =>
      montarNavegacao({
        departamentosVisiveis,
        workflowsPorDept,
        crmV2,
        orcamentoLigado,
        mostrarComercialAvulso,
        comercial: { leads, funil, indicadores, metas, orcamentos, excluir },
        manutencao: {
          indicadores: podeIndicadoresManutencao,
          monitoramento: monitoramentoEnabled && podeMonitoramento,
          os: podeOsDoSistema,
        },
        financeiro: { ver: financeiroVer, agir: financeiroAgir },
        compras,
        comunicados: podeComunicados,
        rh: podeAbrirRh,
        rhLabel: RH.menu,
        isAdmin: user?.isAdmin === true,
      }),
    [
      departamentosVisiveis,
      workflowsPorDept,
      crmV2,
      orcamentoLigado,
      mostrarComercialAvulso,
      leads,
      funil,
      indicadores,
      metas,
      orcamentos,
      excluir,
      podeIndicadoresManutencao,
      podeOsDoSistema,
      monitoramentoEnabled,
      podeMonitoramento,
      financeiroVer,
      financeiroAgir,
      compras,
      podeComunicados,
      podeAbrirRh,
      user?.isAdmin,
    ],
  );
}
